import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationCancellationCode, NavigationEnd, Router } from '@angular/router';

import { NavigationService, UrlService, WINDOW } from '@frontend/vanilla/core';
import { findLast } from 'lodash-es';
import { Observable, Subject, filter } from 'rxjs';

import { NavigationStack } from './navigation-stack';
import { PredefinedStackNames } from './predefined-stack-names';

export interface StackHistoryItem {
    url: string;
    stack: NavigationStack;
}

@Injectable({
    providedIn: 'root',
})
export class NavigationStackHistoryService {
    private _activeStack?: NavigationStack;
    private history: StackHistoryItem[] = [];
    private currentIndex = 0;
    private _historyChange$ = new Subject<{ url: string; stack: NavigationStack | null }>();
    private initialNavUsedReplaceUrl: boolean | null = null;

    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly urlService = inject(UrlService);
    private readonly navigationService = inject(NavigationService);
    private readonly window = inject(WINDOW);
    private readonly nonSportsStack = new NavigationStack({ name: PredefinedStackNames.NonSportsProduct });
    private appRootUrl = '';

    public get historyChange$(): Observable<{ url: string; stack: NavigationStack | null }> {
        return this._historyChange$;
    }

    public init(initialUrl: string, initialStack: NavigationStack, appRootUrl: string): void {
        this.appRootUrl = appRootUrl;

        const firstInitialization = this.history.length === 0;
        if (firstInitialization) {
            this.subscribeForRouteEvents();
        }

        this.history = [{ url: initialUrl, stack: initialStack }];
        this._activeStack = initialStack;
        this._activeStack.history = [initialUrl];
        this.nonSportsStack.history = [];
    }

    public get activeStack(): NavigationStack | undefined {
        return this._activeStack;
    }

    public setActiveStack(newStack: NavigationStack) {
        this.ensureCurrentUrlIsActual();
        this._activeStack = newStack;
    }

    public setActiveStackAndUpdateCurrentHistory(newStack: NavigationStack) {
        this._activeStack = newStack;

        const lastHistoryItem = this.history.at(-1);

        if (lastHistoryItem && lastHistoryItem.stack.name !== newStack.name) {
            lastHistoryItem.stack.history.splice(-1);
            lastHistoryItem.stack.currentUrl = lastHistoryItem.stack.history.at(-1);

            lastHistoryItem.stack = newStack;
        }
    }

    public get lastSportStack(): NavigationStack | undefined {
        return findLast(this.history, (x) => x.stack.name !== PredefinedStackNames.NonSportsProduct)?.stack;
    }

    public goBackStackScoped(): void {
        this.ensureCurrentUrlIsActual();

        if (this.current.stack.parameters.canBeActivatedByNav || this.previous?.stack?.name === this.current.stack.name) {
            this.window.history.back();
            return;
        }

        const previousUrlInCurrentStack = findLast(this.current.stack.history, (x) => x !== this.current.url);
        if (!previousUrlInCurrentStack) {
            this.current.stack.history = [];
            this.navigationService.goTo(this.current.stack.userEntryUrl, { replace: true });
            return;
        }

        const previousUrlIndex = this.current.stack.history.lastIndexOf(previousUrlInCurrentStack);
        this.current.stack.history.splice(previousUrlIndex + 1);
        this.navigationService.goTo(previousUrlInCurrentStack, { replace: true });
    }

    private subscribeForRouteEvents(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationCancel),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => {
                this.initialNavUsedReplaceUrl =
                    event.code === NavigationCancellationCode.SupersededByNewNavigation ? this.getCurrentNavigationParams().usedReplaceUrl : null;
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => this.processRouterEvent(event));
    }

    private get current(): StackHistoryItem {
        return this.history[this.currentIndex];
    }

    private get previous(): StackHistoryItem | null {
        if (this.currentIndex <= 0) {
            return null;
        }
        return this.history[this.currentIndex - 1];
    }

    private get next(): StackHistoryItem | null {
        if (this.currentIndex >= this.history.length) {
            return null;
        }
        return this.history[this.currentIndex + 1];
    }

    private processRouterEvent(event: NavigationEnd): void {
        if (!event.url.startsWith(this.appRootUrl)) {
            this.setActiveStack(this.nonSportsStack);
        }

        const navParams = this.getCurrentNavigationParams();
        const replaceCurrent = this.initialNavUsedReplaceUrl == null ? navParams.usedReplaceUrl : this.initialNavUsedReplaceUrl;

        this.onUrlChange(event.url, navParams.browserInitiated, replaceCurrent);
        this.initialNavUsedReplaceUrl = null;
    }

    private getCurrentNavigationParams(): { usedReplaceUrl: boolean; browserInitiated: boolean } {
        const navState = this.router.getCurrentNavigation();
        const browserInitiated = navState?.trigger === 'popstate';
        const usedReplaceUrl = !!navState?.extras.replaceUrl;

        return { usedReplaceUrl, browserInitiated };
    }

    private onUrlChange(url: string, browserInitiated: boolean, replaceCurrent: boolean): void {
        if (!this.activeStack) {
            return;
        }

        if (browserInitiated) {
            this.processBrowserInitiatedUrlChange(url);
            return;
        }

        this.activeStack.currentUrl = url;

        if (replaceCurrent) {
            this.current.url = url;
            if (this.current.stack.history.length > 0) {
                this.current.stack.history[this.current.stack.history.length - 1] = url;
            }
        } else {
            this.history.splice(this.currentIndex + 1);

            this.history.push({ url, stack: this.activeStack });
            if (this.activeStack.history.at(-1) !== url) {
                this.activeStack.history.push(url);
            }

            this.currentIndex = this.history.length - 1;
        }

        this._historyChange$.next({ ...this.current });
    }

    private processBrowserInitiatedUrlChange(url: string) {
        let newIndex = this.getNewHistoryIndexAfterNav(url);

        if (newIndex === this.currentIndex) {
            // For case if url was changed without events, and we could not update it
            const fallbackUrlWithoutParms = this.getUrlWithoutParams(url);
            newIndex = this.getNewHistoryIndexAfterNav(fallbackUrlWithoutParms);

            if (newIndex === this.currentIndex) {
                this._historyChange$.next({ url, stack: null });
                return;
            }

            this.history[newIndex].url = fallbackUrlWithoutParms;
            this.history[newIndex].stack.currentUrl = fallbackUrlWithoutParms;
        }

        this.currentIndex = newIndex;
        this._activeStack = this.current.stack;
        this._historyChange$.next({ ...this.current });
    }

    private getUrlWithoutParams(url: string): string {
        const params = url.split('?').at(-1) ?? '';
        if (params.length > 1) {
            return url.replace(`?${params}`, '');
        }
        return url;
    }

    private getNewHistoryIndexAfterNav(url: string): number {
        if (url === this.next?.url) {
            return this.currentIndex + 1;
        }
        if (url === this.previous?.url) {
            return this.currentIndex - 1;
        }
        return this.currentIndex;
    }

    // For cases when url was updated silently without firing a router event
    // We can't use it on urlChange, because the state can be inconsistent with browser url
    public ensureCurrentUrlIsActual(): void {
        if (!this.current || !!this.router.getCurrentNavigation()) {
            return;
        }

        const url = this.urlService.current().url();
        this.current.url = url;
        this.current.stack.currentUrl = url;
        if (this.current.stack.history.length > 0) {
            this.current.stack.history[this.current.stack.history.length - 1] = url;
        }
    }
}
